import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Lightbulb, 
  PieChart, 
  Users, 
  Wrench
} from 'lucide-react';

interface MainFunctionOption {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
}

interface MainFunctionCardsProps {
  onSelectFunction: (id: string) => void;
  selectedFunction: string | null;
  options?: MainFunctionOption[];
}

const CardContainer = styled(motion(Box))(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(2.5),
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(5px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 255, 255, 0.3))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
  },
  '&:hover': {
    background: 'rgba(26, 26, 46, 0.8)',
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0, 255, 255, 0.3)',
    border: '1px solid rgba(0, 255, 255, 0.3)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  },
  '@keyframes gradient': {
    '0%': {
      backgroundPosition: '0% 50%'
    },
    '50%': {
      backgroundPosition: '100% 50%'
    },
    '100%': {
      backgroundPosition: '0% 50%'
    }
  }
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.6)',
  border: '2px solid rgba(138, 43, 226, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    border: '2px solid rgba(0, 255, 255, 0.3)',
    boxShadow: '0 0 15px rgba(0, 255, 255, 0.3)',
    background: 'rgba(26, 26, 46, 0.8)',
  }
}));

const SelectedCardContainer = styled(CardContainer)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.8)',
  border: '1px solid rgba(0, 204, 153, 0.5)',
  boxShadow: '0 0 20px rgba(0, 204, 153, 0.3)',
  '&::after': {
    background: 'linear-gradient(90deg, #00CC99, #00FFFF)',
    height: '3px',
  },
}));

// Main function options with rich descriptions
const mainFunctionOptions: MainFunctionOption[] = [
  { 
    id: 'improve', 
    label: 'Improve Problem Statement', 
    description: 'Refine and enhance your problem statement with the help of executive surrogates.',
    icon: <FileText size={24} color="#00FFFF" />
  },
  { 
    id: 'similar', 
    label: 'Similar Problems', 
    description: 'Discover problems that share characteristics with yours and learn from their solutions.',
    icon: <Lightbulb size={24} color="#FFD700" />
  },
  { 
    id: 'solutions', 
    label: 'View Solutions', 
    description: 'Explore proposed and implemented solutions for your problem.',
    icon: <PieChart size={24} color="#00CC99" />
  },
  { 
    id: 'surrogates', 
    label: 'View Surrogates', 
    description: 'Browse AI and human surrogates available to help solve your problem.',
    icon: <Users size={24} color="#FF6F91" />
  },
  { 
    id: 'toolbox', 
    label: 'Problem Solving Toolbox', 
    description: 'Access various methodologies and frameworks to tackle complex problems.',
    icon: <Wrench size={24} color="#8A2BE2" />
  }
];

const MainFunctionCards: React.FC<MainFunctionCardsProps> = ({ 
  onSelectFunction,
  selectedFunction,
  options = mainFunctionOptions
}) => {
  return (
    <Grid container spacing={2}>
      {options.map((option, index) => {
        const CardComponent = selectedFunction === option.id ? SelectedCardContainer : CardContainer;
        
        return (
          <Grid item xs={12} sm={6} md={4} key={option.id}>
            <CardComponent
              onClick={() => onSelectFunction(option.id)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <IconContainer>
                {option.icon}
              </IconContainer>
              
              <Typography 
                variant="h6" 
                sx={{ 
                  color: selectedFunction === option.id ? '#00FFFF' : '#00FFFF',
                  fontWeight: selectedFunction === option.id ? 600 : 500,
                  mb: 1,
                  transition: 'all 0.3s ease',
                }}
              >
                {option.label}
              </Typography>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  flexGrow: 1,
                }}
              >
                {option.description}
              </Typography>
            </CardComponent>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default MainFunctionCards; 